<template>
  <div class="home">
    <BrandHeader :title="eventTitle" />

    <div class="content">
      <!-- Logged-in: greet + stats + progress + CTA -->
      <template v-if="auth.isLoggedIn && me">
        <div class="greeting">
          <div class="g-name">{{ displayName }}</div>
        </div>

        <div v-if="activeMultiplier" class="multiplier-banner">
          <div class="mb-badge">×{{ activeMultiplier.multiplier }}</div>
          <div class="mb-body">
            <div class="mb-title">點數加倍時段進行中</div>
            <div class="mb-sub">結束於 {{ activeMultiplier.endLabel }}</div>
          </div>
        </div>

        <div class="stats">
          <div class="stat">
            <span class="s-label">點數</span>
            <span class="s-value">{{ me.total_points }}</span>
          </div>
        </div>

        <!-- 加好友提示:整句由後台 line_invite 文案控制(含品牌名樣式與
             LINE 連結);留空則不顯示。 -->
        <p v-if="lineInviteHtml" class="hint" v-html="lineInviteHtml"></p>

        <div class="bars">
          <MilestoneBar
            title="消費點數獎勵"
            :value="me.total_points"
            :max="pointsMax"
            :marks="pointsMarks"
            color="green"
          />
        </div>

        <div class="cta-row">
          <van-button
            class="cta cta-fill"
            block
            :color="cyan"
            @click="$router.push('/bars')"
          >
            <svg class="cta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" />
              <circle cx="12" cy="9" r="2.5" />
            </svg>
            前往店家
          </van-button>
          <van-button
            class="cta cta-fill"
            block
            :color="cyan"
            @click="$router.push('/wall')"
          >
            <svg class="cta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <path d="M14 14h3v3M20 14v3M14 20h3M20 20v.01" />
            </svg>
            我的條碼
          </van-button>
        </div>

        <div
          class="banner"
          :class="{ 'has-image': bannerImage }"
          :style="bannerImage ? { backgroundImage: `url(${bannerImage})` } : undefined"
        >
          <div class="banner-inner" v-html="bannerHtml"></div>
        </div>

        <section class="info-card">
          <h3 class="info-title"><span class="info-bar"></span>集點方式</h3>
          <div class="info-body">
            <div v-if="collectRulesHtml" v-html="collectRulesHtml"></div>
          </div>
          <ul v-if="multiplierDates.length" class="window-list">
            <li v-for="d in multiplierDates" :key="d">{{ d }}</li>
          </ul>
        </section>

        <section v-if="lotteryEnabled" class="info-card">
          <h3 class="info-title"><span class="info-bar"></span>抽獎規則</h3>
          <div class="info-body">
            <p class="info-line">每 <strong>{{ pointsPerLottery }} 點</strong> = <span class="hl-blue">1 次抽獎資格</span></p>
            <div v-if="lotteryRulesHtml" v-html="lotteryRulesHtml"></div>
          </div>
          <button class="info-cta" @click="$router.push('/rewards')">
            <svg class="info-cta-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M7 4h10v4a5 5 0 0 1-10 0V4z" />
              <path d="M17 5h2a3 3 0 0 1 0 6h-2M7 5H5a3 3 0 0 0 0 6h2" />
              <path d="M9 13.5V17h6v-3.5M8 21h8M12 17v4" />
            </svg>
            獎項與抽獎結果
          </button>
        </section>

        <section v-if="sponsors.length > 0" class="sponsors">
          <h3 class="sponsors-title">本次贊助</h3>
          <div class="sponsor-marquee" :style="{ '--n': sponsors.length }">
            <div class="marquee-track">
              <!-- Duplicated set: animating translateX(-50%) on the doubled
                   list yields a seamless infinite scroll without JS. -->
              <router-link
                v-for="(s, idx) in marqueeSponsors"
                :key="`${s.id}-${idx}`"
                class="marquee-item"
                :to="`/sponsors/${s.id}`"
              >
                <div class="sponsor-circle">
                  <img
                    v-if="s.image_url"
                    :src="s.image_url"
                    :alt="s.name"
                    @error="onSponsorImgErr"
                  />
                  <span class="sponsor-fallback">{{ s.name }}</span>
                </div>
              </router-link>
            </div>
          </div>
        </section>
      </template>

      <!-- Not logged in: full-screen login CTA -->
      <template v-else>
        <div class="login-screen">
          <div class="ls-mark">
            <img
              :src="brand.stack || '/brand/logo-stack.png'"
              alt="INFINITE"
              class="ls-logo"
              @error="onLogoErr"
            />
            <h1 v-if="eventTitle" class="ls-title">{{ eventTitle }}</h1>
            <div v-else class="ls-title-skeleton" aria-hidden="true"></div>
            <p class="ls-sub">走訪店家,獲得專屬點數與好禮</p>
          </div>
          <template v-if="!liffError">
            <van-button
              block
              :class="['ls-cta', { 'ls-cta-prep': !liffReady }]"
              :color="cyan"
              :disabled="!liffReady"
              :loading="!liffReady"
              loading-type="spinner"
              loading-text="準備登入…"
              @click="login"
            >
              <span class="line-mark">LINE</span>
              使用 LINE 登入
            </van-button>
            <p class="ls-tos">
              通過登入,即表示您同意我們的
              <RouterLink to="/tos">服務條款</RouterLink> 和
              <RouterLink to="/privacy">隱私政策</RouterLink>
            </p>
          </template>
          <template v-else>
            <div class="ls-error">
              <div class="ls-error-icon">⚠</div>
              <h3 class="ls-error-title">無法載入 LINE 登入</h3>
              <p class="ls-error-msg">
                此活動需要透過 LINE App 開啟。<br>
                請先安裝 LINE,再點下方按鈕重試。
              </p>
              <van-button
                block
                class="ls-cta"
                color="#06C755"
                @click="openInLine"
              >
                <span class="line-mark">LINE</span>
                用 LINE 開啟
              </van-button>
              <a
                class="ls-install-link"
                href="https://line.me/zh-hant/download"
                target="_blank"
                rel="noopener"
              >還沒有 LINE?點此下載</a>
            </div>
          </template>
          <div class="ls-foot">INFINITE</div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter, RouterLink } from "vue-router";
import { showFailToast } from "vant";
import liff from "@line/liff";
import api from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { consumePendingDeepLink } from "@/utils/deepLink";
import BrandHeader from "@/components/BrandHeader.vue";
import MilestoneBar from "@/components/MilestoneBar.vue";
import { useEventTitle } from "@/composables/useEventTitle";
import { useEventConfig } from "@/composables/useEventConfig";
import { useBrand } from "@/composables/useBrand";
import { useAppConfig, themeColor } from "@/composables/useAppConfig";

const auth = useAuthStore();
const cyan = themeColor("--color-primary-cyan", "#2BD3BF");
const router = useRouter();

const { liffId: cfgLiffId } = useAppConfig();
const LIFF_ID = cfgLiffId || import.meta.env.VITE_LIFF_ID || "";
const liffReady = ref(false);
const liffError = ref<string | null>(null);

// Open the page inside LINE App via the line.me launcher (works on
// mobile when LINE is installed; otherwise prompts to install).
const LINE_OPEN_URL = LIFF_ID
  ? `https://liff.line.me/${LIFF_ID}`
  : "";
function openInLine() {
  if (LINE_OPEN_URL) window.location.href = LINE_OPEN_URL;
}

interface MeData {
  user: {
    id: number;
    line_display_name: string | null;
    line_picture_url: string | null;
  };
  total_points: number;
  lottery_count: number;
  points_to_next: number;
}

const me = ref<MeData | null>(null);

interface MultiplierWindow {
  start_at: string;
  end_at: string;
  multiplier: number;
}
interface ActiveMultiplier {
  multiplier: number;
  endLabel: string;
}
const multiplierWindows = ref<MultiplierWindow[]>([]);
const now = ref(new Date());
let nowTimer: ReturnType<typeof setInterval> | null = null;

/** Format every >1× window as "M/D(週) HH:mm – 次日 HH:mm" for the
 *  集點方式 card. Pulls straight from the same /api/rules/multiplier-windows
 *  feed so the homepage list stays in sync with whatever the operator sets
 *  in SQLAdmin. */
const WEEKDAYS_ZH = ["日", "一", "二", "三", "四", "五", "六"];
const multiplierDates = computed<string[]>(() => {
  return multiplierWindows.value
    .filter((w) => w.multiplier > 1)
    .map((w) => {
      const s = new Date(w.start_at);
      const e = new Date(w.end_at);
      const m = s.getMonth() + 1;
      const d = s.getDate();
      const wd = WEEKDAYS_ZH[s.getDay()];
      const sH = String(s.getHours()).padStart(2, "0");
      const sM = String(s.getMinutes()).padStart(2, "0");
      const eH = String(e.getHours()).padStart(2, "0");
      const eM = String(e.getMinutes()).padStart(2, "0");
      const crossesDay =
        e.getDate() !== s.getDate() ||
        e.getMonth() !== s.getMonth() ||
        e.getFullYear() !== s.getFullYear();
      const endLabel = crossesDay ? `次日 ${eH}:${eM}` : `${eH}:${eM}`;
      return `${m}/${d}(${wd}) ${sH}:${sM} – ${endLabel}`;
    });
});

const activeMultiplier = computed<ActiveMultiplier | null>(() => {
  const t = now.value.getTime();
  // Pick the highest multiplier currently in [start, end). Aligns with the
  // backend rule (services/points_service.compute_multiplier).
  const active = multiplierWindows.value
    .filter((w) => {
      const s = new Date(w.start_at).getTime();
      const e = new Date(w.end_at).getTime();
      return w.multiplier > 1 && s <= t && t < e;
    })
    .sort((a, b) => b.multiplier - a.multiplier);
  if (active.length === 0) return null;
  const w = active[0];
  const end = new Date(w.end_at);
  // Display end as HH:MM (24h) — server stores datetimes in local Asia/Taipei
  // already (no UTC conversion needed); confirm via APP_TZ in the API.
  const hh = String(end.getHours()).padStart(2, "0");
  const mm = String(end.getMinutes()).padStart(2, "0");
  return { multiplier: w.multiplier, endLabel: `${hh}:${mm}` };
});

const displayName = computed(
  () => me.value?.user?.line_display_name || "會員",
);

/** Points bar tracks progress toward the next 6-point lottery milestone.
 *  Total points has no real cap (multiple visits, multipliers), so a fixed
 *  max would be misleading — same logic as Wallet.vue. */
const pointsMax = computed(() => {
  const p = me.value?.total_points ?? 0;
  return (Math.floor(p / pointsPerLottery.value) + 1) * pointsPerLottery.value;
});
const pointsMarks = computed(() => {
  const p = me.value?.total_points ?? 0;
  const next = pointsMax.value;
  const prev = Math.floor(p / pointsPerLottery.value) * pointsPerLottery.value;
  return prev === 0 ? [0, next] : [0, prev, next];
});

async function loadMe() {
  if (!auth.isLoggedIn) return;
  try {
    const r = await api.get("/me");
    me.value = r.data;
  } catch (e) {
    // Silent; the user can still see the page even if /me transient-fails.
  }
}

// Sponsors list — fetched from /api/sponsors so the organiser can add /
// remove / reorder live in SQLAdmin without a redeploy. Duplicated once
// (marqueeSponsors) so the CSS marquee can loop seamlessly by
// translating -50% (the second half is identical to the first).
interface SponsorOut {
  id: number;
  name: string;
  image_url: string | null;
  link_url: string | null;
}
const sponsors = ref<SponsorOut[]>([]);
const marqueeSponsors = computed(() => [...sponsors.value, ...sponsors.value]);

async function loadSponsors() {
  try {
    const r = await api.get("/sponsors");
    sponsors.value = Array.isArray(r.data) ? r.data : [];
  } catch {
    sponsors.value = [];
  }
}

/** Always show a logo on the login splash. `brand.stack` is null until
 *  /api/brand resolves (and can be a stale/404 URL once it does); fall back
 *  to the bundled default so the mark never renders as an empty hole during
 *  loading — the gap the operator saw on the login screen. */
function onLogoErr(e: Event) {
  const img = e.target as HTMLImageElement;
  const fallback = "/brand/logo-stack.png";
  if (!img.src.endsWith(fallback)) img.src = fallback;
}

/** Hide the broken-image icon when sponsor PNG is missing; the sibling
 *  `.sponsor-fallback` text takes its place via CSS. */
function onSponsorImgErr(e: Event) {
  const img = e.target as HTMLImageElement;
  img.style.display = "none";
  const fallback = img.nextElementSibling as HTMLElement | null;
  if (fallback) fallback.style.display = "inline";
}

async function loadMultiplierWindows() {
  try {
    const r = await api.get("/rules/multiplier-windows");
    multiplierWindows.value = r.data ?? [];
  } catch {
    multiplierWindows.value = [];
  }
}

/** Operator-editable HTML snippets keyed in DB. API shape is
 *  `{blocks: {key: {body_html, image_url}}}`. Defaults kick in when the API
 *  is unreachable / pre-deploy / admin hasn't saved — cards never go blank.
 *  A row's body_html only wins when non-empty; clear-then-save wipes a card. */
const DEFAULT_COLLECT_RULES_HTML =
  '<p class="info-lead">活動期間內,於任一合作店家消費可集點。</p>';
const DEFAULT_LOTTERY_RULES_HTML =
  '<p class="info-line muted">點數越多抽獎機會越多,但每人最多只能中一獎</p>' +
  '<p class="info-line muted">活動結束後公開抽獎</p>';
const DEFAULT_BANNER_HTML =
  '<div class="banner-title">1990 東區跑吧</div>';
// 加好友 hint is now a fully editable 前台文案 block (line_invite). Empty
// default → hidden until /info loads (and stays hidden if the operator
// leaves it blank), matching the old v-if="lineFriendUrl" behaviour.
const DEFAULT_LINE_INVITE_HTML = "";

const collectRulesHtml = ref(DEFAULT_COLLECT_RULES_HTML);
const lotteryRulesHtml = ref(DEFAULT_LOTTERY_RULES_HTML);
const lineInviteHtml = ref(DEFAULT_LINE_INVITE_HTML);
const bannerHtml = ref(DEFAULT_BANNER_HTML);
const bannerImage = ref<string | null>(null);
// Title comes from the shared composable so the login splash, the
// BrandHeader, and the browser tab all track the same admin-edited value.
const { eventTitle } = useEventTitle();
const { pointsPerLottery, lotteryEnabled } = useEventConfig();
const { brand } = useBrand();

interface InfoBlockResp { body_html?: string; image_url?: string | null }
async function loadInfoBlocks() {
  try {
    const r = await api.get("/info");
    const blocks: Record<string, InfoBlockResp> = r.data?.blocks ?? {};
    // The key being PRESENT means the admin has saved this block — respect
    // the saved value even if it's an empty string (i.e. admin deliberately
    // cleared it). Falling back to defaults only when the API didn't return
    // the key at all (rare: pre-deploy / network failure).
    if (blocks.collect_rules) collectRulesHtml.value = blocks.collect_rules.body_html ?? "";
    if (blocks.lottery_rules) lotteryRulesHtml.value = blocks.lottery_rules.body_html ?? "";
    if (blocks.line_invite) lineInviteHtml.value = blocks.line_invite.body_html ?? "";
    if (blocks.home_banner) {
      bannerHtml.value  = blocks.home_banner.body_html ?? "";
      bannerImage.value = blocks.home_banner.image_url ?? null;
    }
    // event_title is loaded by the App-level useEventTitle composable so
    // we don't sync it here too.
  } catch {
    /* defaults already populated above */
  }
}

onMounted(async () => {
  // Multiplier windows are public (no auth needed) and drive the homepage
  // double-points banner; load in parallel with auth bootstrap.
  loadMultiplierWindows();
  loadInfoBlocks();
  loadSponsors();
  // Re-evaluate `activeMultiplier` every minute so the banner disappears
  // when the window expires while the page is still open.
  nowTimer = setInterval(() => {
    now.value = new Date();
  }, 60_000);

  if (!LIFF_ID) {
    liffReady.value = true;
    if (auth.isLoggedIn) await loadMe();
    return;
  }
  try {
    // Race against a 5-second timeout — `liff.init()` can hang silently
    // in external browsers without LINE App (no error thrown, just
    // never resolves), leaving the button stuck loading.
    await Promise.race([
      liff.init({ liffId: LIFF_ID }),
      new Promise((_, rej) =>
        setTimeout(() => rej(new Error("LIFF 初始化逾時 — 請使用 LINE App 開啟")), 5000)
      ),
    ]);
    liffReady.value = true;
    if (liff.isLoggedIn() && !auth.isLoggedIn) {
      await login();
    } else if (auth.isLoggedIn) {
      await loadMe();
    }
  } catch (e: any) {
    // Most common cause: user opened the page outside LINE without
    // the LINE app installed. Switch UI to the dedicated error block
    // instead of leaving the button stuck on its loading spinner.
    liffReady.value = true;
    liffError.value = e?.message || "未知錯誤";
  }
});

onUnmounted(() => {
  if (nowTimer) clearInterval(nowTimer);
});

async function login() {
  if (!liff.isLoggedIn()) {
    liff.login();
    return;
  }
  try {
    const token = liff.getAccessToken();
    if (!token) throw new Error("無法取得 LIFF access token");
    const data = await auth.loginWithLine(token);
    if (data.requires_consent) router.push("/consent");
    else if (data.requires_profile) router.push("/me/profile");
    else {
      // Already-onboarded returning user (e.g. cleared cache, fresh
      // browser). The router guard stashed their deep-link target
      // before sending them here for re-login — send them back.
      const target = consumePendingDeepLink();
      if (target) router.push(target);
      else await loadMe();
    }
  } catch (e: any) {
    showFailToast(`登入失敗:${e?.response?.data?.detail ?? e?.message ?? "未知錯誤"}`);
  }
}

</script>

<style scoped>
.home { min-height: 100vh; background: var(--bg-black); color: var(--text-primary); }
.content { padding: 8px 16px 24px; }

/* Logged-in greeting + stats */
.greeting { padding: 8px 0 16px; }
.g-name { font-size: 22px; font-weight: 600; color: var(--text-primary); }

.multiplier-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(135deg, var(--brand-pink) 0%, var(--brand-orange) 100%);
  color: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  margin: 4px 0 16px;
}
.mb-badge {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  border: 1.5px solid rgba(255, 255, 255, 0.6);
  font-size: 18px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.5px;
}
.mb-body { flex: 1; min-width: 0; }
.mb-title { font-size: 16px; font-weight: 700; letter-spacing: 0.5px; }
.mb-sub { font-size: 12px; opacity: 0.85; margin-top: 2px; }

.stats { display: flex; gap: 32px; padding: 4px 0 12px; }
.stat { display: flex; align-items: baseline; gap: 6px; }
.s-label { color: var(--brand-green); font-size: 14px; }
.stat:nth-child(2) .s-label { color: var(--brand-pink); }
.s-value {
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 600;
  line-height: 1;
  margin-left: 4px;
  text-shadow: 0 0 10px color-mix(in srgb, var(--color-primary-cyan) 40%, transparent);
}

.hint {
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
  margin: 8px 0 24px;
}
.hint a { color: var(--brand-green); text-decoration: underline; }

.bars { display: flex; flex-direction: column; gap: 24px; margin-bottom: 24px; }

.cta-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 24px; }
.cta { height: 56px !important; border-radius: 12px !important; font-weight: 600 !important; }
.cta-fill { box-shadow: var(--glow-primary); }
.cta-outline:hover { box-shadow: var(--glow-primary); }
.cta-icon { width: 18px; height: 18px; margin-right: 8px; vertical-align: -3px; }

.banner {
  /* Spec V1 復興深紫 → 主背景 dark-purple gradient + cyan accent edge.
     Combined with text-shadow halo on title, gives the "neon nightclub
     marquee" feel without going full glassmorphism.
     When admin uploads a custom image (`.has-image`), it replaces the
     gradient via inline background-image; we then darken slightly so the
     text overlay stays legible regardless of image brightness. */
  background:
    linear-gradient(135deg, #1a1a1a 0%, #2e2e2e 50%, #111111 100%);
  background-size: cover;
  background-position: center;
  border: 1px solid color-mix(in srgb, var(--color-primary-cyan) 35%, transparent);
  border-radius: 16px;
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  position: relative;
  box-shadow:
    0 0 24px color-mix(in srgb, var(--color-primary-cyan) 18%, transparent),
    inset 0 0 40px color-mix(in srgb, var(--color-primary-cyan) 6%, transparent);
}
.banner.has-image {
  /* 有上傳主視覺圖時拿掉 cyan 1px 描邊 — 那道邊框會在圖上變成多餘的細框。
     無圖的預設漸層仍保留 .banner 的 cyan 描邊當設計重點。 */
  border-color: transparent;
}
.banner.has-image::before {
  /* Dim overlay so text stays readable on bright photos. */
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.55) 100%);
  pointer-events: none;
}
.banner-inner {
  color: var(--text-primary);
  position: relative;  /* sits above the .has-image dim overlay */
  z-index: 1;
}
.banner :deep(.banner-title) {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1px;
  text-shadow: 0 0 12px color-mix(in srgb, var(--color-accent-light-cyan) 60%, transparent);
}
.banner :deep(.banner-date) {
  font-size: 14px;
  color: var(--color-accent-light-cyan);
  margin-top: 6px;
  letter-spacing: 1px;
  opacity: 0.85;
}

/* Activity info card */
.info-card {
  background: var(--bg-card);
  border-radius: 16px;
  padding: 20px 18px;
  margin-top: 16px;
}
.info-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px;
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-green);
  text-shadow: 0 0 8px color-mix(in srgb, var(--color-primary-cyan) 40%, transparent);
}
.info-bar {
  display: inline-block;
  width: 4px;
  height: 18px;
  background: var(--brand-green);
  border-radius: 2px;
  flex-shrink: 0;
  box-shadow: 0 0 6px color-mix(in srgb, var(--color-primary-cyan) 70%, transparent);
}

/* CTA at the bottom of an info-card — used right now by 抽獎規則 → 獎項與抽獎結果. */
.info-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 16px;
  padding: 12px 16px;
  background: transparent;
  border: 1px solid var(--brand-green);
  border-radius: 10px;
  color: var(--brand-green);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
}
.info-cta:hover { background: color-mix(in srgb, var(--color-primary-cyan) 10%, transparent); box-shadow: var(--glow-primary); }
.info-cta-ico { width: 18px; height: 18px; }
/* .info-lead / .info-line / .hl-* — used inside .info-body which receives
 * v-html'd content from /api/info. Vue scoped-CSS hashes don't propagate
 * to v-html children, so we use :deep() so admin-authored markup picks up
 * the brand styling. */
/* Fallback for admin-authored content that's plain <p>/<a> with no class
 * (the back-office rich-text editor doesn't let operators add classes) —
 * without this it inherits --text-primary (white) and looks unstyled /
 * too bright against the card. Lower specificity than the classed rules
 * below so .info-lead / .info-line still win when present. */
.info-card :deep(.info-body p) {
  margin: 0 0 10px;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.7;
}
.info-card :deep(.info-body p:last-child) { margin-bottom: 0; }
.info-card :deep(.info-body a) {
  color: var(--brand-green);
  text-decoration: underline;
}
.info-card :deep(.info-lead) {
  margin: 0 0 14px;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.7;
}
.info-card :deep(.info-line) {
  margin: 0 0 10px;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.6;
}
.info-card :deep(.info-line:last-child) { margin-bottom: 0; }
.info-card :deep(.info-line strong) { color: var(--text-primary); font-weight: 700; }
.info-card :deep(.info-line.muted) { color: var(--text-mute); font-size: 13px; }
.info-card :deep(.hl-green) {
  color: var(--brand-green);
  font-weight: 700;
  text-shadow: 0 0 6px color-mix(in srgb, var(--color-primary-cyan) 50%, transparent);
}
.info-card :deep(.hl-pink) {
  color: var(--color-accent-pink);
  font-weight: 700;
  text-shadow: 0 0 6px color-mix(in srgb, var(--color-martech-pink) 55%, transparent);
}
.info-card :deep(.hl-blue) {
  color: var(--brand-blue);
  font-weight: 700;
  text-shadow: 0 0 6px color-mix(in srgb, var(--color-secondary-blue) 50%, transparent);
}
.window-list {
  list-style: none;
  padding: 0;
  margin: 0 0 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.window-list li {
  color: var(--text-mute);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}

/* Sponsors */
.sponsors {
  margin: 24px 0 8px;
}
.sponsors-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
  letter-spacing: 2px;
}
.sponsor-marquee {
  overflow: hidden;
  width: 100%;
  /* --n: number of unique sponsors (set inline from SPONSOR_IDS.length).
     Track holds 2N items (duplicated). Each item targets 1/3 of viewport
     so 3 show at once. Therefore:
       track width = 2N items × (viewport / 3) = (2N / 3) × viewport
       item width  = (1 / 2N) × track          = viewport / 3
     translateX -50% always lines up with the start of the duplicate set. */
}
.marquee-track {
  display: flex;
  width: calc(200% * var(--n) / 3);
  /* 3 beats per cycle: each beat is a quick slide (~0.7s) followed by a
     ~2s pause. ease-in-out + the keyframe hold-and-slide pattern gives
     "snap-pause" rhythm instead of constant drift. */
  animation: marquee-rhythm 9s ease-in-out infinite;
}
.marquee-track:hover { animation-play-state: paused; }
.marquee-item {
  width: calc(100% / (2 * var(--n)));
  padding: 0 6px;
  box-sizing: border-box;
  flex-shrink: 0;
}
@keyframes marquee-rhythm {
  /* Three evenly spaced beats covering -50% total. Each beat: slide during
     8% of the cycle, then hold for 25%. For N=3 this aligns perfectly to
     item boundaries (each slide = one logo width). For other N it still
     gives a 3-beat rhythm even if it doesn't snap to logos. */
  0%   { transform: translateX(0); }
  8%   { transform: translateX(-16.667%); }
  33%  { transform: translateX(-16.667%); }
  41%  { transform: translateX(-33.333%); }
  66%  { transform: translateX(-33.333%); }
  75%  { transform: translateX(-50%); }
  100% { transform: translateX(-50%); }
}
.sponsor-circle {
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  background: var(--bg-card);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}
.sponsor-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sponsor-fallback {
  display: none;
  color: var(--text-mute);
  font-size: 12px;
  letter-spacing: 1px;
}

/* Login screen */
.login-screen {
  min-height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  text-align: center;
}
.ls-mark { margin-bottom: 56px; }
.ls-logo { width: 170px; height: auto; margin: 0 auto 14px; display: block; }
.ls-title {
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 8px;
  color: var(--brand-green);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
/* Placeholder shown while the event name is loading. Kept at .ls-title's box
   height (26px font → ~30px line) so the real title pops in with no layout
   jump, and never a flash of a hard-coded wrong event name. A calm brand-cyan
   "breathe" (opacity pulse) reads as an intentional loading affordance rather
   than the hard grey shimmer bar that looked broken on the login splash. */
.ls-title-skeleton {
  height: 30px;
  width: 150px;
  max-width: 60%;
  margin: 0 auto 8px;
  border-radius: 15px;
  background: color-mix(in srgb, var(--color-primary-cyan) 16%, transparent);
  animation: ls-breathe 1.4s ease-in-out infinite;
}
@keyframes ls-breathe {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
}
.ls-sub { color: var(--text-secondary); font-size: 14px; margin: 0; }
.ls-cta {
  height: 56px !important;
  border-radius: 28px !important;
  font-weight: 600 !important;
  width: 100%;
  box-shadow: var(--glow-primary);
}
/* While LIFF initialises the button is disabled+loading. Dim the cyan fill
   (set inline via the `color` prop, hence !important) and drop the glow so it
   reads as "準備登入…" / getting-ready instead of a bare spinner stuck in a
   bright solid pill. Settles into the full-glow CTA once liffReady flips. */
.ls-cta-prep {
  background: color-mix(in srgb, var(--color-primary-cyan) 26%, #000) !important;
  border-color: transparent !important;
  box-shadow: none !important;
}
.ls-cta-prep :deep(.van-button__loading-text) {
  color: color-mix(in srgb, #fff 75%, transparent);
  font-weight: 600;
}
.line-mark {
  display: inline-block;
  background: #fff;
  color: #06C755;
  font-weight: 700;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 8px;
  letter-spacing: 1px;
}
.ls-tos { font-size: 12px; color: var(--text-secondary); margin-top: 16px; }
.ls-tos a { color: var(--text-secondary); text-decoration: underline; }
.ls-foot { margin-top: auto; padding-top: 32px; color: var(--text-mute); font-size: 12px; letter-spacing: 4px; }

/* Error state — LIFF init failed (most likely no LINE App installed). */
.ls-error {
  text-align: center;
  padding: 24px 16px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 165, 92, 0.4);
  border-radius: 14px;
  margin-bottom: 16px;
}
.ls-error-icon {
  font-size: 36px;
  color: #FFA55C;
  margin-bottom: 8px;
  filter: drop-shadow(0 0 8px rgba(255, 165, 92, 0.5));
}
.ls-error-title {
  font-size: 16px;
  font-weight: 700;
  color: #FFA55C;
  margin: 0 0 10px;
  letter-spacing: 2px;
}
.ls-error-msg {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 18px;
}
.ls-install-link {
  display: inline-block;
  margin-top: 14px;
  font-size: 12px;
  color: var(--text-secondary);
  text-decoration: underline;
}
</style>
